import React, { useState } from "react";
import { Users, Plus, Pencil } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";
import CatalogTable from "../components/CatalogTable";
import { CatalogLoadingCard, CatalogErrorCard } from "../components/CatalogStateCard";
import EntityFormDialog from "../components/EntityFormDialog";
import StatusBadge from "../components/StatusBadge";
import { useEmailCatalogEntityList } from "../../../hooks/useEmailCatalogEntityList";
import {
  getEmailMarketingAudienceDefinitions,
  createAudienceDefinition,
  updateAudienceDefinition,
} from "../../../lib/emailMarketingApi";

// EMAILS-IMPL-007: Audience Definition authoring. An Audience Definition is
// a PERMANENT, REUSABLE MARKET CLASSIFICATION (AUD-SPEC-001 §3) -- e.g.
// "Final Expense", "Truck Drivers" -- never a contact list, recipient
// count, GHL membership, or inclusion/exclusion rule set. This panel
// deliberately has no such fields, by governance, not by omission.
// Status uses the real, governed 4-state vocabulary (distinct from
// Campaign/Angle's 6-state one -- AUD-SPEC-001 §9 explicitly does not
// adopt Control/Challenger/Active/Retired for Audience).
export const AUDIENCE_STATUS_VALUES = ["Draft", "Approved", "Deprecated", "Archived"];

function emptyForm() {
  return { name: "", status: "Draft" };
}

export default function AudiencesPanel() {
  const { items, loading, error, mutating, mutationError, clearMutationError, createItem, updateItem } =
    useEmailCatalogEntityList({
      list: getEmailMarketingAudienceDefinitions,
      create: createAudienceDefinition,
      update: updateAudienceDefinition,
      getId: (row) => row.audience_id,
    });

  const [expandedId, setExpandedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm());
  const [editingAudience, setEditingAudience] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());

  const openCreate = () => {
    setCreateForm(emptyForm());
    clearMutationError();
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    try {
      await createItem({ name: createForm.name.trim(), status: createForm.status });
      setCreateOpen(false);
    } catch {
      // handled via mutationError
    }
  };

  const openEdit = (audience) => {
    setEditingAudience(audience);
    setEditForm({ name: audience.name, status: audience.status });
    clearMutationError();
  };

  const submitEdit = async () => {
    try {
      await updateItem(editingAudience.audience_id, {
        name: editForm.name.trim() !== editingAudience.name ? editForm.name.trim() : undefined,
        status: editForm.status !== editingAudience.status ? editForm.status : undefined,
      });
      setEditingAudience(null);
    } catch {
      // handled via mutationError
    }
  };

  if (loading) return <CatalogLoadingCard testId="audiences-loading" />;
  if (error) return <CatalogErrorCard message={error} testId="audiences-error" />;

  return (
    <div className="space-y-4" data-testid="audiences-panel">
      <CatalogTable
        testId="audiences-table"
        title="Audience Definitions"
        icon={Users}
        headerAction={
          <Button
            type="button"
            size="sm"
            className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500"
            onClick={openCreate}
            data-testid="new-audience-button"
          >
            <Plus className="h-4 w-4" /> New Audience Definition
          </Button>
        }
        rows={items}
        getRowKey={(row) => row.audience_id}
        onRowClick={(row) => setExpandedId(expandedId === row.audience_id ? null : row.audience_id)}
        emptyTitle="No Audience Definitions yet"
        emptySubtitle='Create one with "New Audience Definition" — a reusable market classification like "Final Expense" or "Truck Drivers".'
        columns={[
          {
            key: "name",
            label: "Audience",
            render: (row) => (
              <div>
                <div className="text-sm font-medium text-slate-100">{row.name}</div>
                <div className="text-xs text-slate-400">{row.audience_id}</div>
              </div>
            ),
          },
          { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} testId={`audience-status-${row.audience_id}`} /> },
          { key: "version", label: "Version", render: (row) => <span className="font-mono text-sm text-slate-300">{row.version || "—"}</span> },
        ]}
      />

      {items
        .filter((row) => row.audience_id === expandedId)
        .map((row) => (
          <div key={row.audience_id} className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`audience-detail-${row.audience_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                <Detail label="Audience ID" value={row.audience_id} mono />
                <Detail label="Status" value={row.status} />
                <Detail label="Version" value={row.version || "—"} mono />
                <Detail label="Created" value={row.created_at ? new Date(row.created_at).toLocaleString() : "—"} />
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1.5 border-white/10 text-slate-200"
                onClick={() => openEdit(row)}
                data-testid={`edit-audience-${row.audience_id}`}
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
            </div>
          </div>
        ))}

      <EntityFormDialog
        testId="create-audience-dialog"
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New Audience Definition"
        description="A permanent, reusable market classification — who the recipient is, independent of Lead Source, Channel, or Campaign."
        onSubmit={submitCreate}
        submitting={mutating}
        error={mutationError}
        submitLabel="Create Audience Definition"
      >
        <div className="space-y-2">
          <Label htmlFor="audience-name">Name</Label>
          <Input
            id="audience-name"
            value={createForm.name}
            onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Final Expense"
            required
            data-testid="audience-name-input"
          />
        </div>
        <StatusField value={createForm.status} onChange={(status) => setCreateForm((f) => ({ ...f, status }))} testId="audience-status-select" />
      </EntityFormDialog>

      {editingAudience && (
        <EntityFormDialog
          testId="edit-audience-dialog"
          open={!!editingAudience}
          onOpenChange={(open) => !open && setEditingAudience(null)}
          title={`Edit ${editingAudience.audience_id}`}
          onSubmit={submitEdit}
          submitting={mutating}
          error={mutationError}
          submitLabel="Save Changes"
        >
          <div className="space-y-2">
            <Label htmlFor="audience-edit-name">Name</Label>
            <Input
              id="audience-edit-name"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              required
              data-testid="audience-edit-name-input"
            />
          </div>
          <StatusField value={editForm.status} onChange={(status) => setEditForm((f) => ({ ...f, status }))} testId="audience-edit-status-select" />
        </EntityFormDialog>
      )}
    </div>
  );
}

function StatusField({ value, onChange, testId }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={testId}>Status</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={testId} data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {AUDIENCE_STATUS_VALUES.map((s) => (
            <SelectItem key={s} value={s} data-testid={`${testId}-option-${s}`}>
              {s}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={mono ? "font-mono text-slate-200" : "text-slate-200"}>{value}</div>
    </div>
  );
}
